import React, {
  useCallback,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import componentOrElement from 'prop-types-extra/lib/componentOrElement';
import * as ModalComponent from 'react-overlays/lib/Modal';
import classNames from 'classnames';

import createMessagingService from 'services/MessagingService/MessagingService';
import { URL_PATHNAME_UPDATED } from 'services/MessagingService/EventNames';
import { VIDEO_AND_CHAT_CLASS } from 'enum/videoPlayer';

import styles from './Modal.css';


const leftModalZIndex = 1310;
const standardZIndex = 1040;

const modalSizes = [
  'small',
  'medium',
  'large',
  'extraLarge',
  'small full',
  'medium full',
  'large full',
];


const sizeStyles = {
  'small': styles.small,
  'medium': styles.medium,
  'large': styles.large,
  'extraLarge': styles.extraLarge,
  'large full': classNames(styles.full, styles.large),
  'medium full': classNames(styles.full, styles.medium),
  'small full': classNames(styles.full, styles.small),
};

const Modal = ({
  show,
  size,
  close,
  closeWithEsc,
  children,
  onBackdropClick,
  type,
  className,
  showClose,
  container,
  background,
  isLeftMenuModal,
  disableBackdropClick,
  scroll,
  myShopVideo,
  belongToHeader,
  opaque,
  zIndex,
  blurred,
  dataVwoId,
}) => {
  const messagingService = createMessagingService();
  useEffect(() => {
    const closeModalForRouteChange = () => {
      if (show && close) {
        close();
      }
    };
    messagingService.on(URL_PATHNAME_UPDATED, closeModalForRouteChange);

    return () => {
      messagingService.off(URL_PATHNAME_UPDATED, closeModalForRouteChange);
    };
  }, [messagingService, show, close]);

  const backDropClick = useCallback(() => {
    if (disableBackdropClick) {
      return;
    }

    if (onBackdropClick) {
      onBackdropClick();

      return;
    }

    close?.();
  }, [disableBackdropClick, onBackdropClick, close]);

  const {
    fullscreenElement,
    webkitFullscreenElement,
    mozFullScreenElement,
    msFullscreenElement,
  } = document;

  const isInFullscreen = Boolean(
    fullscreenElement ||
    webkitFullscreenElement ||
    mozFullScreenElement ||
    msFullscreenElement
  );
  const theatreModeContainer = document.querySelector(`.${VIDEO_AND_CHAT_CLASS}`);
  const currentContainer = isInFullscreen && theatreModeContainer ? theatreModeContainer : container;

  /**
   * Solution for issue that modals scrolls page at the top always on open.
   * And solved issue that while some scrollable modal is active, is possible to scroll main page under modal.
   * These issues were introduced to support iOS scrollable modal and modal was forced to be attached to
   * app div, so to solve first two issues, I was forced to remove possible action to attach modal to
   * app div in case of scroll modal, and I fixed iOS problems with scrollable modals by adding position fixed.
   * Which is a standard way to deal with modals and iOS since iOS handles only overflow hidden on different way.
   * Retested on simulator of iOS device.
   * Also added scroll handling position since when position is set scroll is reset at top, so this is usual practice
   * for rendering modals correct especially on iOS.
   * Added condition that this works only for scroll true modals, because other
   * difficulties not have issues (expect on iOS background is scrollable, but we had it before this fix).
   * Scroll condition added because I saw issue with captcha modals which are not scroll true,
   * and must avoid position fixed in case of modals with captcha
   * so I decide to nothing change for modals with scroll false, since they not have scrolled on top issue.
   **/
  useEffect(() => {
    let modalShowed = false;
    const { body } = document;
    let header = document.getElementsByTagName('header');

    if (show && scroll) {
      body.style.position = 'fixed';
      body.style.overflow = 'hidden';
      modalShowed = true;
    }

    if (
      header?.length &&
      body.style.overflow === 'hidden' &&
      body.style.paddingRight !== '' &&
      !belongToHeader
    ) {
      header[0].setAttribute(
        'style',
        `left: 0; width: calc(100% - ${body.style.paddingRight});`
      );
    }

    return () => {
      header = document.getElementsByTagName('header');

      if (modalShowed) {
        body.style.removeProperty('position');
        body.style.overflow = null;
      }

      if (header?.length && !belongToHeader) {
        header[0].setAttribute(
          'style',
          ''
        );
      }
    };
  }, [show, scroll, belongToHeader]);

  const backdropClassNames = classNames({
    [styles.backdrop]: true,
    [styles.blackBackDrop]: opaque,
    [styles.blurred]: blurred,
  });

  const modalClassNames = classNames(styles.main, { [styles.centeredModal]: myShopVideo });

  const containerClasses = classNames(
    styles.dialog,
    sizeStyles[size],
    styles[type],
    className,
    styles[background],
    styles[scroll],
  );

  const zIndexStyle = { zIndex: isLeftMenuModal ? leftModalZIndex : zIndex || standardZIndex };

  const onEscapeKeyDown = useCallback(() => {
    if (closeWithEsc) {
      close();
    }
  }, [closeWithEsc, close]);

  return (
    <ModalComponent
      backdropClassName={backdropClassNames}
      className={modalClassNames}
      container={currentContainer}
      data-identifier="modal-window"
      data-vwo-id={dataVwoId}
      show={show}
      style={zIndexStyle}
      onBackdropClick={backDropClick}
      onEscapeKeyDown={onEscapeKeyDown}
    >
      <div className={containerClasses}>
        {showClose && (
          <span className={styles.close} onClick={close}>
            &times;
          </span>
        )}
        {children}
      </div>
    </ModalComponent>
  );
};

Modal.propTypes = {
  size: PropTypes.oneOf(modalSizes).isRequired,
  background: PropTypes.oneOf(['white', 'transparent']),
  belongToHeader: PropTypes.bool,
  blurred: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.array]),
  className: PropTypes.string,
  close: PropTypes.func,
  closeWithEsc: PropTypes.bool,
  container: PropTypes.oneOfType([componentOrElement, PropTypes.func]),
  dataVwoId: PropTypes.string,
  disableBackdropClick: PropTypes.bool,
  isLeftMenuModal: PropTypes.bool,
  myShopVideo: PropTypes.bool,
  opaque: PropTypes.bool,
  scroll: PropTypes.bool,
  show: PropTypes.bool,
  showClose: PropTypes.bool,
  type: PropTypes.oneOf(['full', 'standard']),
  zIndex: PropTypes.number,
  onBackdropClick: PropTypes.func,
};

Modal.defaultProps = {
  blurred: false,
  closeWithEsc: false,
  background: 'white',
  belongToHeader: false,
  disableBackdropClick: false,
  isLeftMenuModal: false,
  myShopVideo: false,
  onBackdropClick: undefined,
  opaque: false,
  zIndex: undefined,
  show: true,
  size: 'small',
  type: 'standard',
  children: null,
  close: () => undefined,
  showClose: true,
  container: document.body,
  className: '',
  scroll: false,
  dataVwoId: undefined,
};

export default Modal;
